import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';

import {
  getOAuthAccessToken,
  integrationsApi,
  tasksApi,
  useInitTodoIntegration,
} from '../../../data-access';
import './GithubAuthCallback.scss';
import { Path } from '../../../routing/path';
import { getTodoOAuthExchangeRedirectUrl, trackEventMixpanel } from '../../../utils';
import { TodoTasksBoard } from '@demind-inc/core';

const GithubAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { initTodoIntegration } = useInitTodoIntegration();

  const hasExchanged = useRef(false);

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [isMobileApp, setIsMobileApp] = useState(false);
  const [redirectDestination, setRedirectDestination] = useState<string>(Path.DASHBOARD);

  const [repositories, setRepositories] = useState<Partial<TodoTasksBoard>[]>([]);
  const [selectedRepoIds, setSelectedRepoIds] = useState<Set<string>>(new Set());

  const [isExchangeInFlight, setIsExchangeInFlight] = useState(false);
  const [isFetchingRepos, setIsFetchingRepos] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [errorState, setErrorState] = useState<{ message: string; fatal: boolean } | null>(null);

  useEffect(() => {
    const exchange = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        navigate(Path.DASHBOARD);
        return;
      }

      if (hasExchanged.current) return;
      hasExchanged.current = true;

      setIsExchangeInFlight(true);
      setErrorState(null);

      try {
        const stateUrl = new URL(state);
        const params = new URLSearchParams(stateUrl.search);
        const redirectTo = params.get('redirectTo') || Path.DASHBOARD;
        const stateUserId = params.get('userId') || '';
        const mobileFlag = params.get('isMobileApp')?.includes('true') ?? false;

        setRedirectDestination(redirectTo);
        setIsMobileApp(mobileFlag);
        setUserId(stateUserId);

        if (!stateUserId) {
          throw new Error('Missing user information.');
        }

        const { data } = await integrationsApi.exchangeCode(
          code,
          'github',
          getTodoOAuthExchangeRedirectUrl(Path.CALLBACK.GITHUB_CALLBACK, searchParams)
        );

        if (!data) {
          throw new Error('No access token returned.');
        }

        const token = (data as { accessToken: string }).accessToken;

        if (!token) {
          throw new Error('No access token returned.');
        }

        setAccessToken(token);
      } catch (error) {
        console.error('Error while exchanging GitHub code', error);
        setErrorState({ message: "Couldn't connect to GitHub — try again?", fatal: true });
        hasExchanged.current = false;
      } finally {
        setIsExchangeInFlight(false);
      }
    };

    exchange();
  }, [searchParams, navigate, initTodoIntegration]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    let isActive = true;
    setIsFetchingRepos(true);

    integrationsApi
      .getOriginalTodoBoards('github', accessToken)
      .then(({ data }) => {
        if (!isActive) return;
        const fetchedRepos = data as Partial<TodoTasksBoard>[];
        setRepositories(fetchedRepos);
        setSelectedRepoIds(
          new Set(fetchedRepos.filter((repo) => !repo.closed).map((repo) => repo.originalBoardId))
        );

        // Fetch existing projects and pre-select repositories that are already connected
        if (!userId) {
          return;
        }
        if (!getOAuthAccessToken()) {
          return [fetchedRepos, null] as const;
        }
        return tasksApi
          .getTodoProjects(userId)
          .then((response) => [fetchedRepos, response] as const)
          .catch((error) => {
            console.warn('Failed to fetch existing GitHub projects', error);
            return [fetchedRepos, null] as const;
          });
      })
      .then((result) => {
        if (!isActive || !result) return;
        const [fetchedRepos, response] = result;
        const projects = (response?.data as TodoTasksBoard[]) || [];
        const githubProjects = projects.filter(
          (project) => project.appFrom === 'github' && !project.closed
        );
        const existingRepoIds = new Set(
          githubProjects.map((project) => project.originalBoardId).filter(Boolean) as string[]
        );

        // Select repositories that match existing originalBoardIds
        const reposToSelect = fetchedRepos
          .map((repo) => repo.originalBoardId)
          .filter((id) => id && existingRepoIds.has(id)) as string[];

        if (reposToSelect.length > 0) {
          setSelectedRepoIds(new Set(reposToSelect));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch GitHub repositories', error);
        if (!isActive) return;
        setErrorState({ message: "Couldn't load your GitHub repositories.", fatal: true });
      })
      .finally(() => {
        if (!isActive) return;
        setIsFetchingRepos(false);
      });

    return () => {
      isActive = false;
    };
  }, [accessToken, userId]);

  const handleToggleSelectAll = () => {
    setSelectedRepoIds((prev) => {
      if (!repositories.length) {
        return prev;
      }

      if (prev.size === repositories.length) {
        return new Set();
      }

      return new Set(repositories.map((repo) => repo.originalBoardId).filter(Boolean) as string[]);
    });
  };

  const handleToggleRepo = (repoId: string) => {
    setSelectedRepoIds((prev) => {
      const next = new Set(prev);

      if (next.has(repoId)) {
        next.delete(repoId);
      } else {
        next.add(repoId);
      }

      return next;
    });
  };

  const handleSync = async () => {
    if (!userId) {
      setErrorState({ message: "We're missing some account info — try connecting again.", fatal: true });
      return;
    }

    const repoIds = Array.from(selectedRepoIds);

    if (!repoIds.length) {
      return;
    }

    try {
      setErrorState(null);
      setIsSyncing(true);
      await initTodoIntegration({
        token: accessToken,
        todoType: 'github',
        userId: userId,
        shouldSyncTasks: false,
        skipRefresh: isMobileApp,
        originalBoardIds: repoIds,
      });

      trackEventMixpanel('connect_todo_auth_done', {
        todoType: 'github',
        fromMobileApp: isMobileApp,
        repositoriesSelected: repoIds.length,
      });

      if (isMobileApp) {
        window.location.href = 'lifestack://todo-callback';
        return;
      }

      navigate(redirectDestination || Path.DASHBOARD);
    } catch (error) {
      console.error('Failed to trigger GitHub sync', error);
      setErrorState({
        message: "Couldn't start syncing those repositories.",
        fatal: false,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  if (isExchangeInFlight && !accessToken && !errorState) {
    return (
      <div className="github-auth-callback">
        <CircularProgress />
      </div>
    );
  }

  const allSelected = repositories.length > 0 && selectedRepoIds.size === repositories.length;
  const isIndeterminate = selectedRepoIds.size > 0 && !allSelected;
  const fatalError = errorState?.fatal;
  const errorMessage = errorState?.message;

  return (
    <div className="github-auth-callback">
      <div className="github-auth-callback__card">
        <Typography variant="h5" component="h1" className="github-auth-callback__title">
          Select GitHub repositories
        </Typography>

        {fatalError ? (
          <div className="github-auth-callback__error">
            <Typography color="error" variant="body2" className="github-auth-callback__error-text">
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(redirectDestination || Path.DASHBOARD)}
            >
              Back to dashboard
            </Button>
          </div>
        ) : (
          <div className="github-auth-callback__body">
            {errorMessage ? (
              <Typography
                color="error"
                variant="body2"
                className="github-auth-callback__error-text"
              >
                {errorMessage}
              </Typography>
            ) : null}

            <Typography variant="body2" className="github-auth-callback__description">
              Choose the repositories you want Lifestack to import tasks from.
            </Typography>

            {repositories.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={allSelected}
                    onChange={handleToggleSelectAll}
                  />
                }
                label="Select all repositories"
                className="github-auth-callback__select-all"
              />
            )}

            <div className="github-auth-callback__list">
              {isFetchingRepos ? (
                <div className="github-auth-callback__loading">
                  <CircularProgress size={24} />
                </div>
              ) : repositories.length ? (
                repositories.map((repo) => {
                  const repoId = repo.originalBoardId;
                  if (!repoId) {
                    return null;
                  }
                  const isChecked = selectedRepoIds.has(repoId);

                  return (
                    <label
                      key={repoId}
                      className="github-auth-callback__repo"
                      htmlFor={`repo-${repoId}`}
                    >
                      <Checkbox
                        id={`repo-${repoId}`}
                        checked={isChecked}
                        onChange={() => handleToggleRepo(repoId)}
                      />
                      <div className="github-auth-callback__repo-info">
                        <span className="github-auth-callback__repo-name">
                          {repo.name || 'Untitled repository'}
                        </span>
                        {(repo as any)?.desc || (repo as any)?.description ? (
                          <span className="github-auth-callback__repo-description">
                            {(repo as any)?.desc || (repo as any)?.description}
                          </span>
                        ) : null}
                      </div>
                    </label>
                  );
                })
              ) : (
                <Typography variant="body2" className="github-auth-callback__empty">
                  No repositories available for import.
                </Typography>
              )}
            </div>

            <div className="github-auth-callback__actions">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSync}
                disabled={selectedRepoIds.size === 0 || isSyncing || !!fatalError}
                className="github-auth-callback__sync-button"
              >
                {isSyncing ? (
                  <>
                    <CircularProgress size={18} className="github-auth-callback__button-spinner" />
                    Syncing repositories
                  </>
                ) : (
                  'Import selected repositories'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GithubAuthCallback;
